import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { StatusBadge } from '../../components/StatusBadge';
import { getMyJobs } from '../../utils/api';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { LogOut, RefreshCw, FileText, Truck } from 'lucide-react';

export default function DriverJobs() {
  const [jobs, setJobs] = useState([]);
  const [initialLoad, setInitialLoad] = useState(true);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const { lastUpdated, refreshing, refresh } = useAutoRefresh(async () => {
    const { data } = await getMyJobs();
    setJobs(data);
    setInitialLoad(false);
  }, 10000); // Poll every 10 seconds for drivers

  const getRef = (r) => r.document_type === 'trip_sheet' ? r.trip_sheet_fields?.trip_sheet_no : r.waybill_fields?.waybill_no;

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="driver-jobs-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/cargodash-logo.svg" alt="CargoDash" className="h-9" />
            <div>
              <h1 className="text-lg font-bold text-[#1A1A2E]">My Jobs</h1>
              <p className="text-xs text-[#6B7280]">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => refresh(true)} disabled={refreshing} className="p-2 text-[#6B7280] hover:text-[#1A1A2E] disabled:opacity-50" data-testid="refresh-jobs">
              <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} />
            </button>
            <button onClick={logout} className="p-2 text-[#6B7280] hover:text-[#DC2626]" data-testid="driver-logout"><LogOut size={20} /></button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-1 flex justify-between items-center text-[10px] text-[#9CA3AF] bg-gray-50 border-b border-gray-100">
          <span>Auto-refreshes every 10s</span>
          {lastUpdated && <span>Last: {lastUpdated.toLocaleTimeString()}</span>}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6">
        {initialLoad ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-2 border-[#0D9488] border-t-transparent"></div></div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-20">
            <Truck className="mx-auto mb-4 text-[#9CA3AF]" size={56} />
            <p className="text-lg font-semibold text-[#6B7280]">No jobs assigned</p>
            <p className="text-sm text-[#9CA3AF] mt-1">New jobs will appear automatically</p>
            <button onClick={() => refresh(true)} className="mt-4 px-5 py-2.5 bg-[#0D9488] text-white rounded-lg font-medium text-sm">Refresh Now</button>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job) => (
              <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-5" data-testid={`job-card-${job.id}`}>
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xl font-bold text-[#1A1A2E]">{getRef(job)}</span>
                      <span className={`px-2 py-0.5 rounded-lg text-xs font-semibold uppercase tracking-wider ${job.document_type === 'trip_sheet' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>
                        {job.document_type === 'trip_sheet' ? 'Trip Sheet' : 'Waybill'}
                      </span>
                    </div>
                    <p className="text-sm text-[#1A1A2E] font-medium">{job.third_party_agent}</p>
                    <p className="text-sm text-[#6B7280]">{job.dispatching_hub}</p>
                  </div>
                  <StatusBadge status={job.status} />
                </div>

                {job.document_type === 'trip_sheet' && job.trip_sheet_fields && (
                  <div className="flex gap-4 text-xs text-[#6B7280] mb-4 border-t border-gray-100 pt-3">
                    <span>Waybills: <strong className="text-[#1A1A2E]">{job.trip_sheet_fields.no_of_waybills}</strong></span>
                    <span>Items: <strong className="text-[#1A1A2E]">{job.trip_sheet_fields.no_of_items}</strong></span>
                    <span>Weight: <strong className="text-[#1A1A2E]">{job.trip_sheet_fields.total_weight} kg</strong></span>
                  </div>
                )}
                {job.document_type === 'waybill' && job.waybill_fields && (
                  <div className="flex gap-4 text-xs text-[#6B7280] mb-4 border-t border-gray-100 pt-3">
                    <span>Description: <strong className="text-[#1A1A2E]">{job.waybill_fields.description}</strong></span>
                    <span>Weight: <strong className="text-[#1A1A2E]">{job.waybill_fields.weight} kg</strong></span>
                  </div>
                )}

                <button onClick={() => navigate(`/driver/job/${job.id}`)} className="w-full h-14 bg-[#0D9488] hover:bg-[#0F766E] text-white rounded-lg font-semibold text-base flex items-center justify-center gap-2 transition-colors" data-testid={`open-job-${job.id}`}>
                  <FileText size={20} /> Open Job
                </button>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
